import { useLocation } from "@reach/router";
import { navigate } from "gatsby";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { signOut } from "../services/auth";

const INACTIVITY_TIMEOUT_S = Number(process.env.GATSBY_KIOSK_TIMEOUT_MS) / 1000;
const WARNING_THRESHOLD_S = Number(process.env.GATSBY_KIOSK_WARNING_MS) / 1000;

console.log("GATSBY_KIOSK_TIMEOUT_MS", Number(process.env.GATSBY_KIOSK_TIMEOUT_MS));
console.log("GATSBY_KIOSK_WARNING_MS", Number(process.env.GATSBY_KIOSK_WARNING_MS));

interface KioskContextType {
  resetKiosk: () => Promise<void>;
  dismissWarning: () => void;
  secondsRemaining: number;
  warningActive: boolean;
}

const Context = createContext<KioskContextType>({
  resetKiosk: async () => {},
  dismissWarning: () => {},
  secondsRemaining: INACTIVITY_TIMEOUT_S,
  warningActive: false,
});

export const KioskProvider = ({ children }: { children: ReactNode }) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningActiveRef = useRef(false);
  const [secondsRemaining, setSecondsRemaining] = useState(INACTIVITY_TIMEOUT_S);
  const [warningActive, setWarningActive] = useState(false);
  const { pathname } = useLocation();
  const timerActive = pathname !== "/kiosk/";

  const resetKiosk = useCallback(async () => {
    await signOut();
    navigate("/kiosk/");
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    warningActiveRef.current = false;
    setWarningActive(false);
    setSecondsRemaining(INACTIVITY_TIMEOUT_S);
  }, []);

  const restartTimer = useCallback(() => {
    if (warningActiveRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    setSecondsRemaining(INACTIVITY_TIMEOUT_S);

    timerRef.current = setTimeout(resetKiosk, Number(process.env.GATSBY_KIOSK_TIMEOUT_MS));

    intervalRef.current = setInterval(() => {
      setSecondsRemaining(s => {
        const next = s <= 1 ? 0 : s - 1;
        if (next <= WARNING_THRESHOLD_S) { warningActiveRef.current = true; setWarningActive(true); }
        if (next <= 0 && intervalRef.current) clearInterval(intervalRef.current);
        return next;
      });
    }, 1000);
  }, [resetKiosk]);

  const dismissWarning = useCallback(() => {
    warningActiveRef.current = false;
    setWarningActive(false);
    restartTimer();
  }, [restartTimer]);

  useEffect(() => {
    if (!timerActive) {
      stopTimer();
      return;
    }

    const events: string[] = [
      // Mouse (desktop)
      "mousemove",
      "mousedown",
      // Keyboard
      "keydown",
      // Touch (iPad/touch screens)
      "touchstart",
      "touchend",
      "touchmove",
      // Scroll
      "scroll",
      // Focus (form inputs, accessibility)
      "focusin",
    ];
    events.forEach(event => window.addEventListener(event, restartTimer, { passive: true }));
    restartTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, restartTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerActive, restartTimer, stopTimer]);

  return (
    <Context.Provider value={{ resetKiosk, dismissWarning, secondsRemaining, warningActive }}>
      {children}
    </Context.Provider>
  );
};

export const useKiosk = () => useContext(Context);
